// Central search index for the entire site.
// Anything listed here becomes discoverable in the top-of-page search bar.
// Each entry MUST have:
//   • id       — unique key
//   • title    — primary label displayed in the dropdown
//   • subtitle — secondary hint (category / snippet)
//   • to       — route to navigate to when the user picks it
//   • group    — which section of results it appears under
//   • keywords — additional search terms (spaces, aliases, synonyms)

import { SERVICES } from "@/data/services";
import { CONDITIONS } from "@/data/conditions";
import { FAQS, PREP_GUIDES as PREP_ARTICLES } from "@/data/faq";
import { PREP_GUIDES } from "@/data/prepGuides";
import { TOPICS as PREVENTIVE_TOPICS } from "@/pages/Preventive";
import { FEATURES as TECHNOLOGY_FEATURES } from "@/pages/Technology";
import { SITE_HIGHLIGHTS } from "@/data/highlights";

const STATIC_PAGES = [
    {
        id: "page-home",
        title: "Home",
        subtitle: "Doctor Kichian's Gastroenterology — overview",
        to: "/",
        group: "Pages",
        keywords: "home landing welcome",
    },
    {
        id: "page-about",
        title: "About Dr. Kichian",
        subtitle: "Bio, credentials, and hospital affiliation",
        to: "/about",
        group: "Pages",
        keywords: "bio biography dr kichian frcpc royal college",
    },
    {
        id: "page-services",
        title: "Services",
        subtitle: "All procedures and consultations offered",
        to: "/services",
        group: "Pages",
        keywords: "procedures endoscopy consultation",
    },
    {
        id: "page-conditions",
        title: "Conditions",
        subtitle: "Digestive conditions we evaluate and treat",
        to: "/conditions",
        group: "Pages",
        keywords: "disease diagnosis conditions",
    },
    {
        id: "page-virtual",
        title: "Virtual consultations",
        subtitle: "How virtual visits work — Insig Health platform",
        to: "/virtual-consultations",
        group: "Pages",
        keywords: "virtual telemedicine video insig zoom online remote",
    },
    {
        id: "page-technology",
        title: "Technology",
        subtitle: "Modern diagnostic and endoscopic equipment",
        to: "/technology",
        group: "Pages",
        keywords: "tech equipment scope camera hd narrow-band imaging",
    },
    {
        id: "page-preventive",
        title: "Preventive care",
        subtitle: "Screening strategies personalized to your risk",
        to: "/preventive-care",
        group: "Pages",
        keywords: "prevention screening colon cancer",
    },
    {
        id: "page-resources",
        title: "Patient resources",
        subtitle: "Guides, checklists, and educational articles",
        to: "/resources",
        group: "Pages",
        keywords: "resources library education",
    },
    {
        id: "page-faq",
        title: "FAQ",
        subtitle: "Frequently asked questions",
        to: "/faq",
        group: "Pages",
        keywords: "question faq help",
    },
    {
        id: "page-referrals",
        title: "Referring physicians",
        subtitle: "Fax referrals to (613) 728-0091",
        to: "/referrals",
        group: "Pages",
        keywords: "referral family doctor physician fax refer",
    },
    {
        id: "page-education",
        title: "Education library",
        subtitle: "Plain-language articles about digestive health",
        to: "/education",
        group: "Pages",
        keywords: "articles learn read library",
    },
];

const PREP_HUB = [
    {
        id: "prep-hub",
        title: "Bowel prep guides",
        subtitle: "Two options: Pico-Salax (most patients) or Peglite (kidney/heart)",
        to: "/resources/bowel-prep-guide",
        group: "Bowel prep",
        keywords: "bowel prep colon prep laxative pico peglite colonoscopy prep",
    },
];

const prepGuideEntries = PREP_GUIDES.map((g) => ({
    id: `prep-${g.slug}`,
    title: `${g.product} bowel prep`,
    subtitle: g.tagline,
    to: `/resources/bowel-prep-guide/${g.slug}`,
    group: "Bowel prep",
    keywords: `${g.product} ${g.alt_names || ""} bowel prep colonoscopy laxative`,
}));

const serviceEntries = SERVICES.map((s) => ({
    id: `service-${s.slug}`,
    title: s.name,
    subtitle: s.tagline || s.short?.slice(0, 90),
    to: `/services/${s.slug}`,
    group: "Services",
    keywords: `${s.name} ${s.tagline || ""} ${s.short || ""} ${(s.benefits || []).join(" ")}`,
}));

const conditionEntries = CONDITIONS.map((c) => ({
    id: `condition-${c.slug}`,
    title: c.name,
    subtitle: c.category ? `${c.category} — ${c.summary?.slice(0, 90) || ""}` : c.summary?.slice(0, 100),
    to: `/conditions/${c.slug}`,
    group: "Conditions",
    keywords: `${c.name} ${c.category || ""} ${c.summary || ""} ${(c.symptoms || []).join(" ")}`,
}));

const prepArticleEntries = (PREP_ARTICLES || []).map((p) => ({
    id: `article-${p.slug}`,
    title: p.title,
    subtitle: p.summary?.slice(0, 100),
    to: `/resources/${p.slug}`,
    group: "Resources",
    keywords: `${p.title} ${p.summary || ""} ${(p.sections || [])
        .map((s) => `${s.heading} ${(s.items || []).join(" ")}`)
        .join(" ")}`,
}));

const faqEntries = FAQS.map((f, i) => ({
    id: `faq-${i}`,
    title: f.q,
    subtitle: f.a.slice(0, 100),
    to: "/faq",
    group: "FAQ",
    keywords: `${f.q} ${f.a}`,
}));

// Small highlighted "topic" cards that live on hub pages.
const preventiveEntries = (PREVENTIVE_TOPICS || []).map((topic, i) => ({
    id: `preventive-${i}`,
    title: topic.key || `topic-${i}`,
    subtitle: "",
    to: "/preventive-care",
    group: "Preventive care",
    keywords: `${topic.key || ""} preventive prevention wellness`,
}));

const technologyEntries = (TECHNOLOGY_FEATURES || []).map((feature, i) => ({
    id: `technology-${i}`,
    title: feature.key || `tech-${i}`,
    subtitle: "",
    to: "/technology",
    group: "Technology",
    keywords: `${feature.key || ""} technology equipment`,
}));

// Info cards on the About page (small highlights).  Duplicated here as static
// data because the About page renders them inline with JSX.
const aboutHighlights = [
    { title: "Medical education", body: "Medical school in Toronto, followed by Internal Medicine training and a Gastroenterology fellowship at the University of Alberta, Edmonton." },
    { title: "Royal College Fellow", body: "Fellow of the Royal College of Physicians and Surgeons of Canada (FRCPC) — Canada's national certifying body for medical specialists." },
    { title: "Hospital affiliation", body: "Staff gastroenterologist at Queensway Carleton Hospital in Ottawa since 2004 — two decades of continuous clinical practice." },
    { title: "30,000+ procedures", body: "Extensive endoscopic experience across colonoscopy, gastroscopy, and therapeutic procedures — volume that translates into calm expertise." },
    { title: "Areas of expertise", body: "Common gastrointestinal and liver diseases — GERD, IBS, IBD, colon cancer screening and prevention, fatty liver disease, and more." },
    { title: "Virtual consultations", body: "Virtual consultations are available for patients in Ottawa and the surrounding area. All procedures are performed at Queensway Carleton Hospital." },
];

const aboutEntries = aboutHighlights.map((h, i) => ({
    id: `about-${i}`,
    title: h.title,
    subtitle: h.body.slice(0, 100),
    to: "/about",
    group: "About",
    keywords: `${h.title} ${h.body} dr kichian bio`,
}));

const highlightEntries = SITE_HIGHLIGHTS.map((h, i) => ({
    id: `highlight-${i}-${(h.title || "").toLowerCase().replace(/\s+/g, "-").slice(0, 40)}`,
    title: h.title,
    subtitle: h.body?.slice(0, 120),
    to: h.to,
    group: h.group || "Highlights",
    keywords: `${h.title} ${h.body || ""} ${h.keywords || ""}`,
}));

export const SEARCH_INDEX = [
    ...STATIC_PAGES,
    ...PREP_HUB,
    ...prepGuideEntries,
    ...serviceEntries,
    ...conditionEntries,
    ...prepArticleEntries,
    ...faqEntries,
    ...preventiveEntries,
    ...technologyEntries,
    ...aboutEntries,
    ...highlightEntries,
];

// Simple, forgiving matcher: matches all tokens of `query` against the entry
// text (title + subtitle + keywords). Returns a light relevance score so we
// can sort matches by best fit.
export function searchSite(query, limit = 12) {
    const q = (query || "").trim().toLowerCase();
    if (!q) return [];
    const tokens = q.split(/\s+/).filter(Boolean);

    const scored = [];
    for (const item of SEARCH_INDEX) {
        const title = (item.title || "").toLowerCase();
        const subtitle = (item.subtitle || "").toLowerCase();
        const keywords = (item.keywords || "").toLowerCase();
        const haystack = `${title} \n ${subtitle} \n ${keywords}`;

        let score = 0;
        let matchedAll = true;
        for (const t of tokens) {
            if (!haystack.includes(t)) {
                matchedAll = false;
                break;
            }
            if (title.startsWith(t)) score += 6;
            else if (title.includes(t)) score += 4;
            if (subtitle.includes(t)) score += 2;
            if (keywords.includes(t)) score += 1;
        }
        if (matchedAll) scored.push({ item, score });
    }

    scored.sort((a, b) => b.score - a.score);
    return scored.slice(0, limit).map((s) => s.item);
}
